// El panel de la DERECHA de la pantalla dedicada (`/chat`): acá y solo acá se
// dibuja una respuesta gráfica.
//
// POR QUÉ SOLO ACÁ. El panel lateral del chat mide ~420px de ancho (y en
// ventanas chicas menos todavía): un gráfico ahí entra recortado o ilegible, y
// además obligaría a partir esa franja en dos. Así que el panel lateral muestra
// un aviso con un botón (ver `BurbujaMensaje`) y el dibujo pasa siempre por esta
// pantalla, donde hay ancho de sobra.
//
// DESKTOP: se monta INLINE, como columna hermana de la conversación. No va por
// portal justamente porque NO es un overlay: es parte del layout de la página y
// tiene que empujar a la conversación, no taparla.
//
// MOBILE: no hay dos columnas posibles, así que es una hoja a pantalla completa
// — y ESA sí va por portal a `document.body`. En la app el panel de contenido de
// desktop es `position: fixed` y cualquier overlay montado dentro de una página
// queda debajo de la sidebar (`project_fixed_panel_stacking_trap`); montarlo en
// `body` lo deja como hermano de la sidebar y su z-index manda de verdad. Aunque
// hoy este caso solo se dé por debajo de `md`, dejarlo dentro de la página sería
// una bomba de tiempo para el día que el breakpoint cambie.

import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useChatAtlas } from '../ChatAtlasContext';
import { ubicarGrafico } from '../graficosChat';
import { useEsEscritorio } from './useEsEscritorio';
import { VisorGrafico } from './VisorGrafico';

/** Encabezado + visor. Es el mismo árbol en las dos vistas: lo único que cambia
 *  es el envoltorio (columna inline vs. hoja a pantalla completa). */
const Contenido: React.FC<{
  id: string;
  titulo: string;
  posicion: number;
  total: number;
  anterior: string | null;
  siguiente: string | null;
  html: string;
  onIr: (id: string) => void;
  onCerrar: () => void;
}> = ({ id, titulo, posicion, total, anterior, siguiente, html, onIr, onCerrar }) => (
  <>
    <header className="flex h-14 shrink-0 items-center gap-1 border-b border-[#02062B]/[0.06] px-3">
      <div className="min-w-0 flex-1 px-1">
        <p className="truncate text-[14px] font-semibold text-brand-black">{titulo}</p>
        {total > 1 && (
          <p className="text-[11.5px] font-medium text-[#02062B]/[0.45]">
            Gráfico {posicion} de {total}
          </p>
        )}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            disabled={!anterior}
            onClick={() => anterior && onIr(anterior)}
            aria-label="Ver el gráfico anterior"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            disabled={!siguiente}
            onClick={() => siguiente && onIr(siguiente)}
            aria-label="Ver el gráfico siguiente"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronRight className="h-[18px] w-[18px]" />
          </button>
        </>
      )}

      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar la respuesta gráfica"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.05] hover:text-brand-black"
      >
        <X className="h-5 w-5" />
      </button>
    </header>

    {/* `key` con el id: al pasar al gráfico siguiente queremos un iframe NUEVO,
        no el mismo con otro `srcDoc`. Reusarlo dejaría vivo el documento viejo
        (y sus timers) hasta que el navegador decida recargar. */}
    <div className="min-h-0 flex-1 overflow-hidden bg-white">
      <VisorGrafico key={id} html={html} titulo={titulo} />
    </div>

    <p className="shrink-0 px-4 py-2 text-[11px] leading-snug text-[#02062B]/[0.4]">
      Lo armó Atlas a partir de la conversación. Revisá los números antes de
      usarlos.
    </p>
  </>
);

export const PanelGraficosChat: React.FC = () => {
  const chat = useChatAtlas();
  const esEscritorio = useEsEscritorio();
  const ubicacion = ubicarGrafico(chat.mensajes, chat.graficoActivoId);

  // El id puede quedar colgado (ej.: "Conversación nueva" con un gráfico
  // abierto). Se limpia en un efecto y no durante el render, que no puede
  // tener efectos.
  const huerfano = chat.graficoActivoId !== null && ubicacion === null;
  const cerrarGrafico = chat.cerrarGrafico;
  useEffect(() => {
    if (huerfano) cerrarGrafico();
  }, [huerfano, cerrarGrafico]);

  if (!ubicacion) return null;

  const contenido = (
    <Contenido
      id={ubicacion.grafico.id}
      titulo={ubicacion.grafico.titulo}
      posicion={ubicacion.posicion}
      total={ubicacion.total}
      anterior={ubicacion.anterior}
      siguiente={ubicacion.siguiente}
      html={ubicacion.grafico.html}
      onIr={chat.abrirGrafico}
      onCerrar={chat.cerrarGrafico}
    />
  );

  if (esEscritorio) {
    return (
      <aside
        aria-label="Respuesta gráfica"
        className="flex w-[46%] min-w-[360px] max-w-[720px] shrink-0 flex-col overflow-hidden rounded-l-[26px] border-l border-[#02062B]/[0.06] bg-white"
      >
        {contenido}
      </aside>
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-label="Respuesta gráfica"
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') chat.cerrarGrafico();
      }}
      className="fixed inset-0 z-[120] flex flex-col bg-white"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {contenido}
    </div>,
    document.body,
  );
};

export default PanelGraficosChat;
